import {
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useGoogleLogin } from '@react-oauth/google'
import { X } from 'lucide-react'
import { useState } from 'react'
import { FaGoogle, FaRegEye, FaRegEyeSlash } from 'react-icons/fa6'
import { FiLock } from 'react-icons/fi'
import { MdOutlineMailOutline } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './index.css'

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 5),
  borderRadius: 16,
  backdropFilter: 'blur(10px)',
  background: 'rgba(255, 255, 255, 0.85)',
  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    width: '100%',
    maxWidth: '100%',
  },
}))

const FormTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.3s',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    fontWeight: 500,
  },
}))

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 0),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
  transition: 'all 0.3s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
  },
}))

const Login = () => {
  const [userData, setUserData] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChange = (e) => {
    const { name, value } = e.target
    setUserData((curr) => ({ ...curr, [name]: value }))
    setErrors((curr) => ({ ...curr, [name]: '' }))
  }

  const validate = () => {
    const newErrors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!userData.email) {
      newErrors.email = 'Email is required'
    } else if (!emailRegex.test(userData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!userData.password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleLogin = () => {
    if (validate()) {
      setLoading(true)
      const config = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      }
      fetch(`${process.env.REACT_APP_SERVER_SOCKET}/api/user/login`, config)
        .then((response) => response.json())
        .then((result) => {
          if (result?.login) {
            localStorage.setItem('token', result?.user?.token)
            toast.success(result.message, { closeButton: false })
            navigate('/')
          } else {
            toast.error(result.message, {
              closeButton: false,
              icon: <X color='white' />,
              style: { color: 'white' },
            })
          }
          setLoading(false)
        })
        .catch((error) => {
          console.error(error)
          toast.error('An error occurred. Please try again.', {
            icon: <X color='white' />,
            style: { color: 'white' },
          })
          setLoading(false)
        })
    }
  }

  const handleGoogleLogin = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/youtube.readonly',
    onSuccess: async (tokenResponse) => {
      const googleAccessToken = tokenResponse.access_token
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_SOCKET}/auth/google`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ googleAccessToken }),
        }
      )
      const result = await res.json()
      localStorage.setItem('token', result?.user?.token)
      localStorage.setItem('ytToken', tokenResponse.access_token)
      navigate('/')
    },
    onError: () => console.log('Login Failed'),
  })

  const handleTogglePassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box className='login-container'>
      <Container
        maxWidth='sm'
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={true} timeout={800}>
          <LoginPaper elevation={3}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                alt='logo'
                src='./black.png'
                style={{
                  height: isMobile ? '50px' : '50px',
                }}
              />
              <Typography
                variant='h4'
                component='h1'
                sx={{
                  fontWeight: 800,
                  mt: 2,
                  background: 'linear-gradient(90deg, #1890ff, #36cfc9)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.5px',
                }}
              >
                Welcome to SMT Player
              </Typography>
            </Box>

            <FormTextField
              fullWidth
              label='Email'
              name='email'
              type='email'
              placeholder='enter your email'
              value={userData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <MdOutlineMailOutline size={20} color='#666' />
                  </InputAdornment>
                ),
              }}
            />

            <FormTextField
              fullWidth
              label='Password'
              name='password'
              placeholder='enter your password'
              type={showPassword ? 'text' : 'password'}
              value={userData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <FiLock size={20} color='#666' />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleTogglePassword}
                      edge='end'
                    >
                      {showPassword ? (
                        <FaRegEyeSlash size={20} />
                      ) : (
                        <FaRegEye size={20} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <StyledButton
              fullWidth
              variant='contained'
              color='primary'
              onClick={handleLogin}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color='inherit' sx={{ mr: 1 }} />
              ) : null}
              Sign In
            </StyledButton>
            <StyledButton
              fullWidth
              variant='contained'
              color='primary'
              onClick={() => handleGoogleLogin()}
              disabled={loading}
              sx={{ mt: 2, display: 'flex', alignItems: 'center' }}
            >
              <FaGoogle />
              <Typography sx={{ ml: 2 }}>Login with Google</Typography>
            </StyledButton>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography
                variant='body3'
                sx={{
                  background: 'linear-gradient(90deg, #1890ff, #36cfc9)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontStyle: 'italic',
                  fontWeight: 800,
                }}
              >
                Log in, Tune in & Jam together.
              </Typography>
            </Box>
          </LoginPaper>
        </Fade>
      </Container>
      <ToastContainer position={isMobile ? 'bottom-center' : 'top-right'} />
    </Box>
  )
}

export default Login;
